import React from "react";
import type { FC, ReactNode } from "react";

import { type IntlFormatters, useIntl } from "react-intl";

import { useRecoilCallback } from "recoil";

import {
  BADGE_KIND_SECONDARY,
  BADGE_TYPE_DEFAULT,
  BADGE_TYPE_SUCCESS,
  Badge,
  ContextMenu,
  Flex,
  Link,
  TEXT_VARIANT_LIGHT,
  Text,
  makeButtonWithMenu,
} from "mailup-web-components";

import IconChevronDownRegular from "Components/icons/IconChevronDownRegular";
import { modalRecipientTrackingOpenCallback } from "Pages/RecipientsList/recoil/callbacks/modalRecipientTracking";

const getBadgeStatus = (tracking: boolean): string => {
  switch (tracking) {
    case true:
      return BADGE_TYPE_SUCCESS;
    default:
      return BADGE_TYPE_DEFAULT;
  }
};

const getBadgeText = (intl: IntlFormatters, tracking: boolean): string => {
  switch (tracking) {
    case true:
      return intl.formatMessage({
        id: "Load.MailupCommon.Active",
      });

    default:
      return intl.formatMessage({
        id: "Load.RecipientsList.NotTrack",
      });
  }
};

interface ButtonComponentProps {
  tracking: boolean;
  icon?: ReactNode;
}

const ButtonComponent: FC<ButtonComponentProps> = ({ tracking, icon }) => {
  const intl = useIntl();

  return (
    <Link alignContent data-mu-test="recipient-status-change" gap="8px">
      <Badge
        className="e-badge--clickable"
        kind={BADGE_KIND_SECONDARY}
        type={getBadgeStatus(tracking)}
        text={
          (
            <Flex alignItems="center" gap="4px" display="inline-flex">
              {getBadgeText(intl, tracking)}
              <span style={{ position: "relative", top: "-1px" }}>{icon}</span>
            </Flex>
          ) as unknown as string
        }
      />

      <Text variant={TEXT_VARIANT_LIGHT} alignContent>
        <IconChevronDownRegular width="12px" />
      </Text>
    </Link>
  );
};

interface MenuComponentProps {
  tracking: boolean;
  id: number;
}

const MenuComponent: FC<MenuComponentProps> = ({ tracking, id }) => {
  const intl = useIntl();
  const modalOpen = useRecoilCallback(modalRecipientTrackingOpenCallback, []);

  const items = [
    {
      label: intl.formatMessage({
        id: "Load.MailupCommon.Active",
      }),
      value: true,
      disabled: tracking === true,
      onClick: () => {
        if (tracking !== undefined) {
          void modalOpen(id, tracking);
        }
      },
    },
    {
      label: intl.formatMessage({
        id: "Load.RecipientsList.NotTrack",
      }),
      value: false,
      disabled: tracking === false,
      onClick: () => {
        if (tracking !== undefined) {
          void modalOpen(id, tracking);
        }
      },
    },
  ];

  return <ContextMenu items={items} selected={tracking} />;
};

interface EditStatusProps {
  id: number;
  tracking: boolean;
  icon?: ReactNode;
}

const withButtonProps =
  // eslint-disable-next-line react/display-name
  (tracking: boolean, icon?: ReactNode) => () => (
    <ButtonComponent tracking={tracking} icon={icon} />
  );

const withMenuProps =
  (id: number, tracking: boolean) =>
  // eslint-disable-next-line react/display-name
  () => <MenuComponent tracking={tracking} id={id} />;

const EditStatusTracking: FC<EditStatusProps> = ({ id, tracking, icon }) => {
  const Ctrl = makeButtonWithMenu(
    withButtonProps(tracking, icon),
    withMenuProps(id, tracking),
  );

  return (
    <Flex display="inline-flex">
      <Ctrl
        //data-mu-test={`recipient-edit-status--${channel}`}
        direction="down"
      />
    </Flex>
  );
};

export default EditStatusTracking;
